'use client'
import * as Tabs from 'primitives/tabs'
import * as React from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { tw } from 'utils/classnames'

export function CrafterTabs({
  slug,
  initialTab,
  children,
}: React.PropsWithChildren<{ slug: string; initialTab: string }>) {
  const router = useRouter()
  const pathname = usePathname()

  // Derive active tab from pathname - this is the source of truth
  const getActiveTabFromPath = React.useCallback(() => {
    // Remove trailing slash and split
    const cleanPath = pathname.replace(/\/$/, '')
    const segments = cleanPath.split('/').filter(Boolean)

    // Find the index of the slug in the path
    // The path structure is: [locale?]/crafter/[slug]/[tab?]
    const slugIndex = segments.findIndex(seg => seg === slug)

    if (slugIndex === -1) {
      // eslint-disable-next-line no-console
      console.warn(
        '[CrafterTabs] Slug not found in pathname:',
        pathname,
        'segments:',
        segments,
        'slug:',
        slug,
      )
      return 'posts' // Fallback if slug not found
    }

    // Check if there's a segment after the slug
    const nextSegment = segments[slugIndex + 1]

    // Valid tab values
    const validTabs = ['posts', 'about', 'favorites', 'followers', 'following']

    if (nextSegment && validTabs.includes(nextSegment)) {
      return nextSegment
    }

    // If no segment after slug, or it's not a valid tab, it's the posts tab
    return 'posts'
  }, [pathname, slug])

  // Always use pathname-derived tab as source of truth
  // Only use initialTab on very first render if pathname hasn't loaded yet
  const activeTabFromPath = React.useMemo(() => getActiveTabFromPath(), [getActiveTabFromPath])
  const [isFirstRender, setIsFirstRender] = React.useState(true)

  React.useEffect(() => {
    setIsFirstRender(false)
  }, [])

  const activeTab =
    isFirstRender && !activeTabFromPath ? initialTab || 'posts' : activeTabFromPath || 'posts'

  const tabs = [
    {
      value: 'posts',
      // TODO: Remove mocked counter and replace with actual count
      label: 'Posts',
      isVisible: true,
    },
    {
      value: 'about',
      label: 'About',
      isVisible: true,
    },
    {
      value: 'followers',
      label: 'Followers',
      isVisible: true,
    },
    {
      value: 'following',
      label: 'Following',
      isVisible: true,
    },
  ]

  const visibleTabs = tabs.filter(tab => tab.isVisible)

  const handleTabChange = (tabValue: string) => {
    // Prevent navigation if already on this tab
    if (tabValue === activeTab) {
      return
    }

    // Build target path by modifying current pathname
    // Current pathname might be: /en/crafter/slug or /en/crafter/slug/about
    // We want: /en/crafter/slug or /en/crafter/slug/{tabValue}
    const cleanPath = pathname.replace(/\/$/, '')
    const segments = cleanPath.split('/').filter(Boolean)

    // Find slug index
    const slugIndex = segments.findIndex(seg => seg === slug)

    if (slugIndex === -1) {
      // eslint-disable-next-line no-console
      console.error('[CrafterTabs] Slug not found, cannot navigate')
      return
    }

    // Build new path: keep everything up to and including slug, then add tab if not posts
    const pathUpToSlug = segments.slice(0, slugIndex + 1).join('/')
    const targetPath = tabValue === 'posts' ? `/${pathUpToSlug}` : `/${pathUpToSlug}/${tabValue}`

    // Use startTransition to ensure smooth navigation
    React.startTransition(() => {
      router.push(targetPath, { scroll: false })
    })
  }

  return (
    <>
      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List
          className={tw(
            'lg:max-w-[756px] flex w-full gap-2 mt-5 lg:mt-0 tablet:mx-auto xl:mx-0',
            // MOBILE horizontal scroll
            'overflow-x-auto whitespace-nowrap scrollbar-hide px-1 -mx-1',
          )}
        >
          {visibleTabs.map(tab => (
            <Tabs.Trigger
              key={tab.value}
              value={tab.value}
              className={tw(
                'rounded-full text-cf-purple-900 text-14 !font-semibold py-2.5 px-4',
                // oxlint-disable-next-line no-negated-condition
                tab.value !== activeTab
                  ? 'hover:bg-cf-grey-100 active:bg-cf-grey-200'
                  : 'bg-cf-purple-100 hover:bg-cf-purple-200 active:bg-cf-purple-50',
              )}
            >
              <p> {tab.label} </p>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {children}
      </Tabs.Root>
    </>
  )
}
